import { useDebouncedCallback } from "use-debounce";

import { useLocalStorage } from "../../../../../Common/Hooks/UseLocalStorage";

import { useAsyncEffect } from "./UseAsyncEffect";

type JsonBinHook<T> = [T, (value: T) => void];

export const useJsonBin = <T>(binId: string, initialValue: T): JsonBinHook<T> => {
    const client = new JsonBinRecordClient<T>("https://api.jsonbin.io/v3/b/" + binId);
    const [currentValue, setCurrentValue] = useLocalStorage<T>("jsonbin-cached." + binId, initialValue);
    const flushUpdates = useDebouncedCallback(async () => {
        await client.update(currentValue);
    }, 1000);
    const setValue = (value: T) => {
        setCurrentValue(value);
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        flushUpdates();
    };
    useAsyncEffect(async signal => {
        const value = await client.get();
        if (signal.aborted) {
            return;
        }
        // @ts-ignore
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        setCurrentValue(value?.record);
    }, []);

    return [currentValue, setValue];
};

export class JsonBinRecordClient<T> {
    private readonly binUrl: string;

    public constructor(binUrl: string) {
        this.binUrl = binUrl;
    }

    public async update(value: T): Promise<void> {
        const response = await fetch(this.binUrl, {
            method: "PUT",
            body: JSON.stringify(value),
            headers: {
                "Content-Type": "application/json",
            },
        });
        if (!response.ok) {
            throw new Error(`CODE ${response.status}`);
        }
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return response.json();
    }

    public async get(): Promise<T> {
        const response = await fetch(this.binUrl + "/latest", { method: "GET" });
        if (!response.ok) {
            throw new Error(`CODE ${response.status}`);
        }
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return response.json();
    }
}
